// Copyright 2026 andrewzeck
// 
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
// 
//     https://www.apache.org/licenses/LICENSE-2.0
// 
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { useState, useEffect, useMemo } from "react";
import ModalBasic from "@/components/ModalBasic.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { addToast } from "@/store/app/action.js";
import { useDispatch } from "react-redux";
import { createPlan, updatePlan } from "@/api/commissions.js";
import { getMerchants } from "@/api/admin.js";

function CommissionPlanModal({ isOpen, setIsOpen, plan, onSave }) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showAppliedSellers, setShowAppliedSellers] = useState(false);
  const [allSellers, setAllSellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingSellers, setLoadingSellers] = useState(false);
  const isEditMode = Boolean(plan?._id);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    product_category: 'all',
    commission_type: 'percentage',
    commission_rate: '',
    tiers: [],
    after_max_tier_rate: '',
    after_max_tier_increment: '',
    active: true,
    sellers: []
  });

  // Initialize form when plan changes
  useEffect(() => {
    setShowAppliedSellers(false);
    setSearchTerm("");
    if (plan) {
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        product_category: plan.product_category || 'all',
        commission_type: plan.commission_type || 'percentage',
        commission_rate: plan.commission_rate || '',
        tiers: plan.tiers || [],
        after_max_tier_rate: plan.after_max_tier_rate || '',
        after_max_tier_increment: plan.after_max_tier_increment || '',
        active: plan.active !== undefined ? plan.active : true,
        commission_rate_display: plan.commission_type === 'percentage' ? plan.commission_rate * 100 : plan.commission_rate,
        sellers: plan.sellers || []
      });
    } else {
      // Reset form for new plan
      setFormData({
        name: '',
        description: '',
        product_category: 'all',
        commission_type: 'percentage',
        commission_rate: '',
        tiers: [],
        after_max_tier_rate: '',
        after_max_tier_increment: '',
        active: true,
        sellers: []
      });
    }
  }, [plan, isOpen]);

  const handleChange = (e) => {
    const { name, type, checked } = e.target;
    let value = e.target.value;
    if (name === 'commission_rate_display' && type === 'number') {
      if (formData.commission_type === 'percentage' && value > 1) {
        formData.commission_rate = value / 100;
      }
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTierChange = (index, field, value) => {
    const newTiers = [...formData.tiers];
    newTiers[index] = {
      ...newTiers[index],
      [field]: field === 'name' ? value : (value === '' ? '' : Number(value))
    };
    setFormData(prev => ({ ...prev, tiers: newTiers }));
  };

  const addTier = () => {
    setFormData(prev => ({
      ...prev,
      tiers: [...prev.tiers, { min: '', max: '', rate: '', name: '' }]
    }));
  };

  const removeTier = (index) => {
    setFormData(prev => ({
      ...prev,
      tiers: prev.tiers.filter((_, i) => i !== index)
    }));
  };

  // Fetch all sellers when showing the applied sellers view
  useEffect(() => {
    if (showAppliedSellers && isOpen) {
      fetchAllSellers();
    }
  }, [showAppliedSellers, isOpen]);

  const fetchAllSellers = async () => {
    try {
      setLoadingSellers(true);
      const res = await getMerchants(1, 1000, 'marketplace', false);
      let sellers = res.merchants[0].locations;
      setAllSellers(sellers);
    } catch (error) {
      console.error('Error fetching sellers:', error);
      dispatch(addToast({ message: 'Failed to fetch sellers', type: 'error' }));
    } finally {
      setLoadingSellers(false);
    }
  };

  // Filter sellers based on search term
  const filteredAppliedSellers = useMemo(() => {
    if (!formData.sellers) return [];
    return formData.sellers
      .filter((sellerId) => {
        const seller = allSellers.find(s => s.id === sellerId);
        if (!seller) return false;
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          seller.store_id?.toLowerCase().includes(term) ||
          seller.name?.toLowerCase().includes(term) ||
          seller.subname?.toLowerCase().includes(term)
        );
      })
      .map(sellerId => allSellers.find(s => s.id === sellerId))
      .filter(Boolean);
  }, [formData.sellers, allSellers, searchTerm]);

  const filteredAvailableSellers = useMemo(() => {
    return allSellers
      .filter((seller) => {
        // Exclude already applied sellers
        if (formData.sellers?.includes(seller.id)) return false;
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          seller.store_id?.toLowerCase().includes(term) ||
          seller.name?.toLowerCase().includes(term) ||
          seller.subname?.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => {
        const nameA = (a.subname || a.name || '').toLowerCase();
        const nameB = (b.subname || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [allSellers, formData.sellers, searchTerm]);

  const handleAddSeller = (sellerId) => {
    setFormData(prev => ({
      ...prev,
      sellers: [...(prev.sellers || []), sellerId]
    }));
  };

  const handleRemoveSeller = (sellerId) => {
    setFormData(prev => ({
      ...prev,
      sellers: (prev.sellers || []).filter(id => id !== sellerId)
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      dispatch(addToast({ message: 'Plan name is required', type: 'error' }));
      return false;
    }

    if (formData.commission_type === 'tiered') {
      if (formData.tiers.length === 0) {
        dispatch(addToast({ message: 'At least one tier is required for tiered commissions', type: 'error' }));
        return false;
      }
      for (let tier of formData.tiers) {
        if (tier.min === '' || tier.rate === '') {
          dispatch(addToast({ message: 'All tiers must have min and rate values', type: 'error' }));
          return false;
        }
      }
    } else {
      if (formData.commission_rate === '' || formData.commission_rate <= 0) {
        dispatch(addToast({ message: 'Commission rate is required and must be greater than 0', type: 'error' }));
        return false;
      }
    }
    if (formData.commission_type === 'percentage' && formData.commission_rate > 1) {
      formData.commission_rate = formData.commission_rate / 100;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        commission_rate: formData.commission_type !== 'tiered' ? Number(formData.commission_rate) : undefined,
        after_max_tier_rate: formData.commission_type === 'tiered' && formData.after_max_tier_rate ? Number(formData.after_max_tier_rate) : undefined,
        after_max_tier_increment: formData.commission_type === 'tiered' && formData.after_max_tier_increment ? Number(formData.after_max_tier_increment) : undefined,
        tiers: formData.commission_type === 'tiered' ? formData.tiers : undefined
      };

      let result;
      if (isEditMode) {
        result = await updatePlan(plan._id, submitData);
        dispatch(addToast({ message: 'Commission plan updated successfully', type: 'success' }));
      } else {
        result = await createPlan(submitData);
        dispatch(addToast({ message: 'Commission plan created successfully', type: 'success' }));
      }

      onSave?.(result);
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving commission plan:', error);
      dispatch(addToast({ 
        message: error.response?.data?.message || 'Failed to save commission plan', 
        type: 'error' 
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalBasic
      modalOpen={isOpen}
      setModalOpen={setIsOpen}
      title={
        showAppliedSellers 
          ? 'Manage Applied Sellers' 
          : (isEditMode ? 'Edit Commission Plan' : 'Create Commission Plan')
      }
      width="3xl"
    >
      {showAppliedSellers ? (
        /* Seller Management View */
        <div className="space-y-4">
          {/* Plan Info */}
          <div className="text-sm text-slate-600 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700 pb-3">
            <div className="font-medium text-slate-800 dark:text-slate-100 mb-1">{formData.name}</div>
            <div>
              {formData.commission_type === 'tiered' 
                ? `${formData.tiers?.length || 0} tiers`
                : formData.commission_type === 'percentage'
                ? `${(formData.commission_rate * 100).toFixed(0)}% commission`
                : `$${formData.commission_rate} commission`
              }
            </div>
          </div>

          {/* Search Input */}
          <div>
            <input
              type="text"
              placeholder="Search by store name or store ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input w-full"
            />
          </div>

          {loadingSellers ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2"></div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Loading sellers...</p>
            </div>
          ) : (
            <>
              {/* Applied Sellers Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  Applied Sellers ({formData.sellers?.length || 0})
                </h3>
                <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md">
                  {filteredAppliedSellers.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                      {searchTerm ? 'No applied sellers match your search' : 'No sellers applied to this plan yet'}
                    </div>
                  ) : (
                    filteredAppliedSellers.map((seller) => (
                      <div
                        key={seller.id}
                        className="p-3 border-b border-slate-100 dark:border-slate-700 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {seller.subname || seller.name}
                            </div>
                            {seller.store_id && (
                              <div className="text-sm text-slate-500 dark:text-slate-400">
                                Store ID: {seller.store_id}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveSeller(seller.id)}
                            className="ml-2 text-rose-500 hover:text-rose-600 text-sm font-medium"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Available Sellers Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  Available Sellers
                </h3>
                <div className="max-h-64 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-md">
                  {filteredAvailableSellers.length === 0 ? (
                    <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                      {searchTerm ? 'No available sellers match your search' : 'All sellers have been applied to this plan'}
                    </div>
                  ) : (
                    filteredAvailableSellers.map((seller) => (
                      <div
                        key={seller.id}
                        className="p-3 border-b border-slate-100 dark:border-slate-700 last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                        onClick={() => handleAddSeller(seller.id)}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {seller.subname || seller.name}
                            </div>
                            {seller.store_id && (
                              <div className="text-sm text-slate-500 dark:text-slate-400">
                                Store ID: {seller.store_id}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddSeller(seller.id);
                            }}
                            className="ml-2 text-indigo-500 hover:text-indigo-600 text-sm font-medium"
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="flex justify-end space-x-2 mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
            <SecondaryButton
              type="button"
              onClick={() => {
                setShowAppliedSellers(false);
                setSearchTerm("");
              }}
            >
              Back to Plan
            </SecondaryButton>
            <PrimaryButton
              type="button"
              onClick={async () => {
                setShowAppliedSellers(false);
                setSearchTerm("");
                // Save the changes
                if (isEditMode) {
                  try {
                    setLoading(true);
                    const submitData = {
                      ...formData,
                      commission_rate: formData.commission_type !== 'tiered' ? Number(formData.commission_rate) : undefined,
                      after_max_tier_rate: formData.commission_type === 'tiered' && formData.after_max_tier_rate ? Number(formData.after_max_tier_rate) : undefined,
                      after_max_tier_increment: formData.commission_type === 'tiered' && formData.after_max_tier_increment ? Number(formData.after_max_tier_increment) : undefined,
                      tiers: formData.commission_type === 'tiered' ? formData.tiers : undefined
                    };
                    const result = await updatePlan(plan._id, submitData);
                    dispatch(addToast({ message: 'Applied sellers updated successfully', type: 'success' }));
                    onSave?.(result);
                  } catch (error) {
                    console.error('Error updating applied sellers:', error);
                    dispatch(addToast({ 
                      message: error.response?.data?.message || 'Failed to update applied sellers', 
                      type: 'error' 
                    }));
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              loading={loading}
            >
              Save Changes
            </PrimaryButton>
          </div>
        </div>
      ) : (
        /* Plan Edit Form */
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="name">
              Plan Name <span className="text-rose-500">*</span>
            </label>
            <input
              id="name"
              name="name"
              className="form-input w-full"
              type="text"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Standard Tiered Plan"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              className="form-textarea w-full"
              rows="3"
              value={formData.description}
              onChange={handleChange}
              placeholder="Optional description of the plan"
            />
          </div>

          {/* Product Category */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="product_category">
              Product Category <span className="text-rose-500">*</span>
            </label>
            <select
              id="product_category"
              name="product_category"
              className="form-select w-full"
              value={formData.product_category}
              onChange={handleChange}
              required
            >
              <option value="all">All Products</option>
              <option value="tobacco">Tobacco Only</option>
              <option value="non-tobacco">Non-Tobacco Only</option>
            </select>
          </div>

          {/* Commission Type */}
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="commission_type">
              Commission Type <span className="text-rose-500">*</span>
            </label>
            <select
              id="commission_type"
              name="commission_type"
              className="form-select w-full"
              value={formData.commission_type}
              onChange={handleChange}
              required
            >
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
              <option value="tiered">Tiered</option>
            </select>
          </div>

          {/* Commission Rate (for percentage/fixed) */}
          {formData.commission_type !== 'tiered' && (
            <div>
              <label className="block text-sm font-medium mb-1" htmlFor="commission_rate">
                Commission Rate <span className="text-rose-500">*</span>
                {formData.commission_type === 'percentage' && (
                  <span className="text-xs text-slate-500 ml-1">(as percentage, e.g., 5 for 5%)</span>
                )}
                {formData.commission_type === 'fixed' && (
                  <span className="text-xs text-slate-500 ml-1">(as dollar amount)</span>
                )}
              </label>
              <input
                id="commission_rate_display"
                name="commission_rate_display"
                className="form-input w-full"
                type="number"
                step={formData.commission_type === 'percentage' ? '0.01' : '0.01'}
                min="0"
                value={formData.commission_rate_display}
                onChange={handleChange}
                placeholder={formData.commission_type === 'percentage' ? '5' : '50.00'}
                required
              />
            </div>
          )}

          {/* Tiered Commission Settings */}
          {formData.commission_type === 'tiered' && (
            <div className="space-y-4">
              <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    Commission Tiers
                  </h3>
                  <button
                    type="button"
                    onClick={addTier}
                    className="btn-sm bg-indigo-500 hover:bg-indigo-600 text-white"
                  >
                    + Add Tier
                  </button>
                </div>

                {formData.tiers.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">
                    No tiers added yet. Click "Add Tier" to create one.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {formData.tiers.map((tier, index) => (
                      <div
                        key={index}
                        className="bg-slate-50 dark:bg-slate-800/50 rounded p-3 space-y-2"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            Tier {index + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeTier(index)}
                            className="text-rose-500 hover:text-rose-600 text-sm"
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              Min Sales <span className="text-rose-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-input w-full text-sm"
                              value={tier.min}
                              onChange={(e) => handleTierChange(index, 'min', e.target.value)}
                              placeholder="0"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              Max Sales
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-input w-full text-sm"
                              value={tier.max}
                              onChange={(e) => handleTierChange(index, 'max', e.target.value)}
                              placeholder="Optional"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              Rate ($) <span className="text-rose-500">*</span>
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="form-input w-full text-sm"
                              value={tier.rate}
                              onChange={(e) => handleTierChange(index, 'rate', e.target.value)}
                              placeholder="5"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium mb-1">
                              Tier Name
                            </label>
                            <input
                              type="text"
                              className="form-input w-full text-sm"
                              value={tier.name}
                              onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                              placeholder="e.g., Bronze"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* After Max Tier Settings */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="after_max_tier_rate">
                    After Max Tier Rate ($)
                  </label>
                  <input
                    id="after_max_tier_rate"
                    name="after_max_tier_rate"
                    className="form-input w-full"
                    type="number"
                    min="0"
                    value={formData.after_max_tier_rate}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                  <p className="text-xs text-slate-500 mt-1">Rate applied after highest tier</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" htmlFor="after_max_tier_increment">
                    After Max Tier Increment
                  </label>
                  <input
                    id="after_max_tier_increment"
                    name="after_max_tier_increment"
                    className="form-input w-full"
                    type="number"
                    min="0"
                    value={formData.after_max_tier_increment}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                  <p className="text-xs text-slate-500 mt-1">Increment for after max calculations</p>
                </div>
              </div>
            </div>
          )}

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="active"
              name="active"
              type="checkbox"
              className="form-checkbox"
              checked={formData.active}
              onChange={handleChange}
            />
            <label htmlFor="active" className="ml-2 text-sm font-medium">
              Active Plan
            </label>
          </div>
        </div>

          {/* Modal footer */}
          <div className="flex flex-wrap justify-end space-x-2 mt-6">
            <SecondaryButton
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={loading}
            >
              Cancel
            </SecondaryButton>
            {isEditMode && (
              <SecondaryButton
                type="button"
                onClick={() => setShowAppliedSellers(true)}
                disabled={loading}
              >
                Change Applied Sellers
              </SecondaryButton>
            )}
            <PrimaryButton
              type="submit"
              loading={loading}
            >
              {isEditMode ? 'Update Plan' : 'Create Plan'}
            </PrimaryButton>
          </div>
        </form>
      )}
    </ModalBasic>
  );
}

export default CommissionPlanModal;
